import AppAuth from "./app-auth";

// Request types
export type GenerateRegexRequest = {
    description: string;
    match_examples: string;
    non_match_examples: string;
};

export type GenerateRoiOperationRequest = {
    query: string;
    model: string;
    tier: string;
    user_thread_id?: string;
};

export type ValidateRoiOperationsRequest = {
    operations: string;
};

// Response types
export type GenerateRegexResponse = {
    success: boolean;
    regex: string;
};

export type GenerateRoiOperationResponse = {
    success: boolean;
    operation: string;
    validation_status: string;
    user_thread_id: string;
    processing_time: number;
};

export type ValidateRoiOperationsResponse = {
    success: boolean;
    results: Array<{
        operation: string;
        valid: boolean;
        error: string;
    }>;
};

export type RoiModelsResponse = {
    success: boolean;
    models: string[];
};

export type FetchRoiModelsParams = {
    tier: string;
};

async function portalFetch<T>(appAuth: AppAuth, baseUrl: string, path: string, method: string, body?: unknown): Promise<T> {
    if (!baseUrl) {
        throw new Error('Assistant API URL is not configured. Please set assistantApiUrl in config.json.');
    }

    const url = `${baseUrl}${path}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options: any = {
        method,
        headers: {
            'Accept': 'application/json',
        },
    };
    if (body !== undefined) {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
    }

    const response = await appAuth.fetch(url, options, { noClientId: true, skipDefaultHeaders: true });
    if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Assistant API error (${response.status}): ${errorText}`);
    }

    return await response.json() as T;
}

export async function generateRegex(appAuth: AppAuth, baseUrl: string, request: GenerateRegexRequest): Promise<GenerateRegexResponse> {
    return portalFetch<GenerateRegexResponse>(appAuth, baseUrl, '/generate-regex', 'POST', request);
}

export async function generateRoiOperation(appAuth: AppAuth, baseUrl: string, request: GenerateRoiOperationRequest): Promise<GenerateRoiOperationResponse> {
    return portalFetch<GenerateRoiOperationResponse>(appAuth, baseUrl, '/generate-roi-operation', 'POST', request);
}

export async function validateRoiOperations(appAuth: AppAuth, baseUrl: string, request: ValidateRoiOperationsRequest): Promise<ValidateRoiOperationsResponse> {
    return portalFetch<ValidateRoiOperationsResponse>(appAuth, baseUrl, '/validate-roi-operations', 'POST', request);
}

export async function fetchRoiModels(appAuth: AppAuth, baseUrl: string, params: FetchRoiModelsParams): Promise<RoiModelsResponse> {
    return portalFetch<RoiModelsResponse>(appAuth, baseUrl, `/roi-models?tier=${encodeURIComponent(params.tier)}`, 'GET');
}
